import moment from "moment"
import "./DateRangeSelector.scss"
import React, {FC, useEffect} from "react"
import { DateField, TextInputField } from "../Forms/fields"
import { Button, ButtonGroup } from "reactstrap"
import TodayIcon from '@material-ui/icons/Today';
import DateRangeIcon from '@material-ui/icons/DateRange';
import CalendarMonthIcon from '@material-ui/icons/CalendarToday';
import ArrowBack from '@material-ui/icons/ArrowBack';
import ArrowForward from '@material-ui/icons/ArrowForward';
import ToolTip from "../ToolTip"
import { useTranslations } from "../../store/translations/hooks"
import { TK } from "../../store/translations/translationKeys"
import { useHistory, useLocation } from "react-router-dom"

export interface DateRangeSelectorState {
    from: string
    to: string
    type: 'day'|'month'|'range'
}

export interface DateRangeSelectorProps {
    onRangeChanged: (state: DateRangeSelectorState) => void
    defaultType?: 'day'|'month'|'range'
    defaultStartDay?: string 
    defaultEndDay?: string
    defaultMonth?: string
    style?: React.CSSProperties, 
}

export const DateRangeSelector : FC<DateRangeSelectorProps> = ({
    onRangeChanged,
    style,
    defaultType,
    defaultStartDay,
    defaultEndDay,
    defaultMonth,
}) => {

    const history = useHistory()
    const location = useLocation()
    const t = useTranslations()

    // Helper function to update URL params
    const updateUrlParams = React.useCallback((updates: Record<string, string>) => {
        const params = new URLSearchParams(location.search)
        Object.entries(updates).forEach(([key, value]) => {
            params.set(key, value)
        })
        history.push({ search: params.toString() })
    }, [location.search, history])

    // Get values from URL or defaults
    const queryParams = new URLSearchParams(location.search)
    const today = new Date().toISOString().substr(0, 10)
    const currentMonth = new Date().toISOString().substr(0, 7)

    const validTypes: Array<'day' | 'month' | 'range'> = ['day', 'month', 'range']
    const typeFromUrl = queryParams.get('dateType') as 'day' | 'month' | 'range' | null
    const selectType: 'day' | 'month' | 'range' =
        (typeFromUrl && validTypes.includes(typeFromUrl)) ? typeFromUrl :
        (defaultType || 'month')

    const selectedDay = queryParams.get('day') || defaultStartDay || today
    const selectedEndDay = queryParams.get('endDay') || defaultEndDay || today
    const selectedMonth = queryParams.get('month') || defaultMonth || currentMonth

    // Functions to update individual values
    const setSelectType = React.useCallback((type: 'day' | 'month' | 'range') => {
        updateUrlParams({ dateType: type })
    }, [updateUrlParams])

    const setSelectedDay = React.useCallback((day: string) => {
        updateUrlParams({ day })
    }, [updateUrlParams])

    const setSelectedEndDay = React.useCallback((day: string) => {
        updateUrlParams({ endDay: day })
    }, [updateUrlParams])

    const setSelectedMonth = React.useCallback((month: string) => {
        updateUrlParams({ month })
    }, [updateUrlParams])


    const onWindowChanged = React.useCallback((from: string, to: string) => {
        if(!from || !to || from > to) return
        onRangeChanged({from, to, type: selectType})
    }, [selectType])

    useEffect(() => {
        if(selectType === 'day') {
            onWindowChanged(selectedDay, selectedDay)
        } if (selectType === 'range') {
            onWindowChanged(selectedDay, selectedEndDay)
        } if (selectType === 'month') {
            const from = `${selectedMonth}-01`
            const to = moment.utc(selectedMonth).endOf('month').toISOString().substr(0, 10)
            onWindowChanged(from, to)
        }
    }, [selectType, selectedDay, selectedEndDay, selectedMonth])

    useEffect(() => {if(defaultType) setSelectType(defaultType)}, [defaultType])
    useEffect(() => {if(defaultStartDay) setSelectedDay(defaultStartDay)}, [defaultStartDay])
    useEffect(() => {if(defaultEndDay) setSelectedEndDay(defaultEndDay)}, [defaultEndDay])
    useEffect(() => {if(defaultMonth) setSelectedMonth(defaultMonth)}, [defaultMonth])

    const moveIntervalWindow = React.useCallback((forward: boolean) => {
        if(selectType === 'day' || selectType === 'range') {
            const newDay = moment.utc(selectedDay)
                .add(forward ? 1 : -1, 'days')
                .toISOString().substr(0, 10)
            const newEndDay = moment.utc(selectedEndDay)
                .add(forward ? 1 : -1, 'days')
                .toISOString().substr(0, 10)
            updateUrlParams({ day: newDay, endDay: newEndDay })
        } else {
            const newMonth = (forward
                ? moment.utc(selectedMonth).endOf('month').add(1, 'days')
                : moment.utc(selectedMonth).startOf('month').add(-1, 'days')).toISOString().substr(0, 7)
            updateUrlParams({ month: newMonth })
        }
    }, [selectType, selectedDay, selectedEndDay, selectedMonth, updateUrlParams])
            

  return (
    <div className="dateSelectionPanel" style={style}>
        {(selectType === 'day' || selectType === 'range') &&
            <DateField
                value={selectedDay}
                type="date"
                onChange={setSelectedDay}
            />
        }
        {selectType === 'range' && 
            <DateField
                value={selectedEndDay}
                type="date"
                onChange={setSelectedEndDay}
            />
        }
        {selectType === 'month' &&
            <TextInputField
                value={selectedMonth}
                type="month"
                onChange={setSelectedMonth}
            />
        }
        <ButtonGroup>
            <Button id='day-button' onClick={() => setSelectType('day')} active={selectType === 'day'}><TodayIcon /></Button>
            <Button id='range-button' onClick={() => setSelectType('range')} active={selectType === 'range'}><DateRangeIcon /></Button>
            <Button id='month-button' onClick={() => setSelectType('month')} active={selectType === 'month'}><CalendarMonthIcon /></Button>
            <Button onClick={() => moveIntervalWindow(false)}><ArrowBack /></Button>
            <Button onClick={() => moveIntervalWindow(true)}><ArrowForward /></Button>

            <ToolTip target='day-button' toolTipContent={t(TK.day)}/>
            <ToolTip target='range-button' toolTipContent={t(TK.interval)}/>
            <ToolTip target='month-button' toolTipContent={t(TK.month)}/>
        </ButtonGroup>
    </div>
  )
}
